import { useEffect, useMemo, useRef, useState } from 'react';
import { useVideoTags } from '../../../../hooks/useVideoTags';
import * as prospectMediaService from '../../../../services/prospect-media';
import type { VideoTag } from '../../../../types/prospect-media';
import { ConfirmModal } from '../../../../components/ConfirmModal';
import './VideoTagEditor.css';

interface VideoTagEditorProps {
  prospectId: string;
  videoId: string;
  /** The video's current labels, as last returned by the API. */
  tags: VideoTag[];
  /** Called with the authoritative list after every successful save. */
  onTagsChange: (tags: VideoTag[]) => void;
  /** Called after a tag is deleted for the whole staff, so other videos on screen can drop it. */
  onTagDeleted?: (tagId: string) => void;
}

/**
 * Toggle-to-apply label picker for a single video.
 *
 * Each toggle sends the whole selection (the API takes set semantics), and the UI updates
 * optimistically so a click feels instant. A failed save rolls back to the last list the
 * server confirmed rather than leaving the pill in a state that isn't persisted.
 */
export const VideoTagEditor = ({
  prospectId,
  videoId,
  tags,
  onTagsChange,
  onTagDeleted,
}: VideoTagEditorProps) => {
  const { tags: vocabulary, loading, createTag, deleteTag } = useVideoTags();
  // Edit mode turns each pill into a delete button, the way the phone's tag picker does.
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState<VideoTag | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>(() => tags.map((t) => t.id));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newTagName, setNewTagName] = useState('');
  const [addingTag, setAddingTag] = useState(false);

  // The last selection the server confirmed. Rollback target when a save fails.
  const committedIds = useRef<string[]>(tags.map((t) => t.id));
  // The same confirmed list as tag objects, so a delete can report what is left without
  // reading a `tags` prop captured before a queued save landed.
  const committedTags = useRef<VideoTag[]>(tags);
  // Where focus goes after a delete removes the pill that opened the confirm.
  const editToggleRef = useRef<HTMLButtonElement>(null);
  const newTagInputRef = useRef<HTMLInputElement>(null);
  // Set when the coach cancels a delete that is still waiting behind a save, so it never goes out.
  const deleteCancelled = useRef(false);

  // Saves run one at a time, in the order the user clicked. Each PUT carries the whole
  // selection, so two in flight at once can commit out of order and the older one silently
  // wins — a tag the user just added disappears with no error to explain it.
  const saveChain = useRef<Promise<void>>(Promise.resolve());

  // Saves still in the queue. The spinner clears when the last one lands, not the first.
  const pending = useRef(0);

  // Depend on the joined ids, not the array. Callers routinely pass `video.tags ?? []`,
  // which is a fresh array on every render — keying off identity would reset the
  // selection mid-save and undo the optimistic toggle the user just made.
  const tagIdsKey = tags.map((t) => t.id).join(',');

  useEffect(() => {
    const ids = tagIdsKey ? tagIdsKey.split(',') : [];
    setSelectedIds(ids);
    committedIds.current = ids;
    committedTags.current = tags;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the ids; see tagIdsKey above.
  }, [videoId, tagIdsKey]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const save = (nextIds: string[]): Promise<void> => {
    setSelectedIds(nextIds);
    setError(null);

    pending.current += 1;
    setSaving(true);

    const queued = saveChain.current.then(async () => {
      try {
        const response = await prospectMediaService.setVideoTags(prospectId, videoId, nextIds);
        committedIds.current = response.tags.map((t) => t.id);
        committedTags.current = response.tags;
        setSelectedIds(committedIds.current);
        onTagsChange(response.tags);
      } catch (err) {
        console.error('[VideoTagEditor] Failed to save video tags', err);
        setSelectedIds(committedIds.current);
        setError('Could not save tags. Please try again.');
      } finally {
        pending.current -= 1;
        if (pending.current === 0) setSaving(false);
      }
    });

    // The chain must survive a rejection, or one failed save wedges every later one.
    saveChain.current = queued.catch(() => undefined);
    return queued;
  };

  const toggleTag = (tagId: string) => {
    const next = selectedSet.has(tagId)
      ? selectedIds.filter((id) => id !== tagId)
      : [...selectedIds, tagId];
    save(next);
  };

  const handleConfirmDelete = async () => {
    const tag = confirmingDelete;
    if (!tag || deleting) return;
    deleteCancelled.current = false;
    setDeleting(true);
    setError(null);

    // The DELETE waits behind any save still queued, so a save carrying this tag can never reach
    // the server after it. The local cleanup then works from the newest confirmed list.
    const run = saveChain.current.then(async () => {
      if (deleteCancelled.current) return false;
      try {
        await deleteTag(tag.id);
      } catch (err) {
        console.error('[VideoTagEditor] Failed to delete video tag', err);
        setError(`Could not delete "${tag.name}". Please try again.`);
        return false;
      }
      // The server already hides a deleted tag on every video, so there is no save to send.
      setSelectedIds((current) => current.filter((id) => id !== tag.id));
      if (committedIds.current.includes(tag.id)) {
        committedTags.current = committedTags.current.filter((t) => t.id !== tag.id);
        committedIds.current = committedTags.current.map((t) => t.id);
        onTagsChange(committedTags.current);
      }
      onTagDeleted?.(tag.id);
      return true;
    });
    saveChain.current = run.then(() => undefined, () => undefined);

    const deleted = await run.catch(() => false);
    setDeleting(false);
    if (deleteCancelled.current) return;
    setConfirmingDelete(null);
    if (deleted) {
      // Nothing left to edit: go back to tagging.
      if (vocabulary.every((t) => t.id === tag.id)) setEditing(false);
      // The pill that opened the confirm is gone; put focus on the Edit/Done toggle (or, once the
      // last tag is gone and the toggle with it, the new-tag box) instead of the page.
      requestAnimationFrame(() => (editToggleRef.current ?? newTagInputRef.current)?.focus());
    }
  };

  const handleAddTag = async (e: React.FormEvent) => {
    e.preventDefault();

    const name = newTagName.trim();
    if (!name || addingTag) return;

    setAddingTag(true);
    setError(null);

    try {
      const created = await createTag(name);
      setNewTagName('');
      // A brand-new label is only useful if it lands on the video you made it for.
      await save([...selectedIds, created.id]);
    } catch (err) {
      console.error('[VideoTagEditor] Failed to create video tag', err);
      setError(
        err instanceof Error && err.message
          ? err.message
          : 'Could not create that tag. Please try again.'
      );
    } finally {
      setAddingTag(false);
    }
  };

  return (
    <div className="video-tag-editor">
      <div className="video-tag-editor__header">
        <span className="video-tag-editor__label" id={`video-tags-label-${videoId}`}>
          Tags
        </span>
        {saving && <span className="video-tag-editor__status">Saving…</span>}
        {!loading && vocabulary.length > 0 && (
          <button
            ref={editToggleRef}
            type="button"
            className="video-tag-editor__edit"
            aria-pressed={editing}
            onClick={() => setEditing((current) => !current)}
          >
            {editing ? 'Done' : 'Edit tags'}
          </button>
        )}
      </div>

      {loading ? (
        <p className="video-tag-editor__loading">Loading tags…</p>
      ) : (
        <div
          className="video-tag-editor__tags"
          role="group"
          aria-labelledby={`video-tags-label-${videoId}`}
        >
          {vocabulary.map((tag) => {
            const isSelected = selectedSet.has(tag.id);
            if (editing) {
              return (
                <button
                  key={tag.id}
                  type="button"
                  className="video-tag-editor__tag video-tag-editor__tag--delete"
                  aria-label={`Delete ${tag.name}`}
                  onClick={() => setConfirmingDelete(tag)}
                >
                  {tag.name}
                  <span aria-hidden="true" className="video-tag-editor__delete-mark">×</span>
                </button>
              );
            }
            return (
              <button
                key={tag.id}
                type="button"
                className={`video-tag-editor__tag ${
                  isSelected ? 'video-tag-editor__tag--selected' : ''
                }`}
                aria-pressed={isSelected}
                onClick={() => toggleTag(tag.id)}
              >
                {tag.name}
              </button>
            );
          })}
        </div>
      )}

      <form className="video-tag-editor__add" onSubmit={handleAddTag}>
        <input
          ref={newTagInputRef}
          className="video-tag-editor__input"
          type="text"
          value={newTagName}
          maxLength={30}
          placeholder="New tag"
          aria-label="New tag name"
          onChange={(e) => setNewTagName(e.target.value)}
        />
        <button
          className="video-tag-editor__add-button"
          type="submit"
          disabled={!newTagName.trim() || addingTag}
        >
          Add
        </button>
      </form>

      {error && (
        <p className="video-tag-editor__error" role="alert">
          {error}
        </p>
      )}

      <ConfirmModal
        isOpen={confirmingDelete !== null}
        onClose={() => {
          // A delete still waiting behind a save has not been sent yet; cancelling stops it.
          if (deleting) deleteCancelled.current = true;
          setConfirmingDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title={confirmingDelete ? `Delete "${confirmingDelete.name}"?` : 'Delete tag?'}
        message="This removes it from your tag list and from every video that has it, for your whole staff. Add a tag with the same name to bring it back."
        confirmText={deleting ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        busy={deleting}
      />
    </div>
  );
};
